import { supabase } from '@/integrations/supabase/client';
import type { SearchCompsResult } from '@/services/ai';

export interface EbayItemSummary {
  itemId: string;
  title: string;
  price?: { value: string; currency: string };
  condition?: string;
  itemWebUrl?: string;
  image?: { imageUrl: string };
}

export interface EbaySearchResult {
  itemSummaries?: EbayItemSummary[];
  total?: number;
  href?: string;
}

export interface EbayListingPayload {
  sku?: string;
  title: string;
  description: string;
  price: number;
  quantity?: number;
  condition?: string;
  /** eBay category ID. Required — `create-listing` rejects '0' or missing. */
  categoryId: string;
  imageUrls?: string[];
  merchantLocationKey?: string;
  /** Item specifics keyed by aspect name. Values are arrays because
   *  eBay aspects can be multi-valued (e.g. Color: ['Red', 'Blue']).
   *  When omitted, the proxy falls back to `{Brand: ['Unbranded']}`
   *  which only works for unbranded categories. */
  aspects?: Record<string, string[]>;
  /** Optional per-listing policy overrides; falls back to the cached
   *  policies on platform_connections when omitted. */
  fulfillment_policy_id?: string;
  payment_policy_id?: string;
  return_policy_id?: string;
  /** When true, the proxy re-uploads each imageUrl to eBay's
   *  Site-Hosted Pictures (EPS) before creating the inventory item,
   *  so listings survive even if the source bucket goes down. A
   *  per-image upload failure falls back to the caller-hosted URL
   *  rather than aborting the publish. */
  host_on_ebay?: boolean;
  /** Sprint 16: listing format. Defaults to FIXED_PRICE which uses
   *  Good-Til-Canceled. AUCTION requires `auction_start_price` and
   *  optionally takes `auction_reserve_price`,
   *  `auction_buy_it_now_price`, and `listing_duration` (DAYS_1/3/5/7/10). */
  format?: 'FIXED_PRICE' | 'AUCTION';
  /** Starting bid amount for auction listings (in marketplace
   *  currency). Required when format='AUCTION'. */
  auction_start_price?: number;
  /** Optional reserve price for auction listings — the bid that must
   *  be hit before the auction "meets reserve". eBay enforces some
   *  category/marketplace restrictions on reserve use. */
  auction_reserve_price?: number;
  /** Optional Buy-It-Now price for auction listings — lets buyers
   *  skip the auction and purchase outright. */
  auction_buy_it_now_price?: number;
  /** Auction duration in days, prefixed: 'DAYS_1', 'DAYS_3', 'DAYS_5',
   *  'DAYS_7' (most common, default), 'DAYS_10', 'DAYS_21', 'DAYS_30'. */
  listing_duration?: 'DAYS_1' | 'DAYS_3' | 'DAYS_5' | 'DAYS_7' | 'DAYS_10' | 'DAYS_21' | 'DAYS_30';
}

export interface EbayListingResult {
  ok: boolean;
  offerId?: string;
  sku?: string;
  listingId?: string;
  /** Count of images that fell back to caller-hosted URLs because EPS
   *  upload failed (only populated when `host_on_ebay: true` was
   *  requested; 0 means every image is on EPS or `host_on_ebay` was
   *  not requested). The UI uses this to surface a "N images still
   *  bucket-hosted" warning. */
  epsFallbacks?: number;
}

export interface EbayConnectionStatus {
  connected: boolean;
  scope: string | null;
  connectedAt: string | null;
  /** eBay marketplace this connection is bound to (EBAY_US, EBAY_GB, etc).
   *  Drives currency + region for every API call. */
  marketplaceId: string | null;
  /** True when all 3 eBay business policies are cached locally. Required
   *  for any create-listing call to succeed. */
  policiesReady: boolean;
  /** Names of policies missing from the local cache, if any. */
  missingPolicies: Array<'fulfillment' | 'payment' | 'return'>;
  policiesFetchedAt: string | null;
}

/** Client-side mirror of supabase/functions/_shared/marketplace.ts — used
 *  by the Settings dropdown when picking a marketplace pre-OAuth.
 *  Keep in sync with the edge-function module; both are tiny. */
export interface EbayMarketplaceOption {
  id: string;
  label: string;
  currency: string;
  country: string;
}

export const EBAY_MARKETPLACES: EbayMarketplaceOption[] = [
  { id: 'EBAY_US', label: 'United States', currency: 'USD', country: 'US' },
  { id: 'EBAY_GB', label: 'United Kingdom', currency: 'GBP', country: 'GB' },
  { id: 'EBAY_CA', label: 'Canada (English)', currency: 'CAD', country: 'CA' },
  { id: 'EBAY_AU', label: 'Australia', currency: 'AUD', country: 'AU' },
  { id: 'EBAY_DE', label: 'Germany', currency: 'EUR', country: 'DE' },
  { id: 'EBAY_FR', label: 'France', currency: 'EUR', country: 'FR' },
  { id: 'EBAY_IT', label: 'Italy', currency: 'EUR', country: 'IT' },
  { id: 'EBAY_ES', label: 'Spain', currency: 'EUR', country: 'ES' },
  { id: 'EBAY_IE', label: 'Ireland', currency: 'EUR', country: 'IE' },
  { id: 'EBAY_AT', label: 'Austria', currency: 'EUR', country: 'AT' },
];

export interface EbayCategorySuggestion {
  categoryId: string;
  categoryName: string;
  /** Human-readable breadcrumb, e.g. "Antiques > Glass > Carnival Glass". */
  path: string;
}

/**
 * A required aspect (item specific) for an eBay category, as returned
 * by the taxonomy API's get_item_aspects_for_category endpoint. We only
 * surface aspects with `aspectRequired === true` — surfacing every
 * optional one would overwhelm the listing review UI.
 */
export interface EbayRequiredAspect {
  /** Human-facing label, e.g. "Brand", "Color", "Material". */
  name: string;
  /** FREE_TEXT lets the user type any value; SELECTION_ONLY requires
   *  picking from `values`. */
  mode: 'FREE_TEXT' | 'SELECTION_ONLY';
  /** Data type — drives input field selection in the UI. */
  dataType: 'STRING' | 'NUMBER' | 'DATE';
  /** Predefined values when `mode === 'SELECTION_ONLY'`, or
   *  suggested values when FREE_TEXT. */
  values: string[];
}

// sell.finances: reconcileFinances reads payout transactions for real fees
// (fl-b1u). A connection made before it was added must reconnect to get it.
const EBAY_SCOPES = [
  'https://api.ebay.com/oauth/api_scope/sell.inventory',
  'https://api.ebay.com/oauth/api_scope/sell.fulfillment',
  'https://api.ebay.com/oauth/api_scope/sell.account',
  'https://api.ebay.com/oauth/api_scope/sell.finances',
].join(' ');

function isSandbox(): boolean {
  return import.meta.env.VITE_EBAY_SANDBOX === 'true';
}

/** BroadcastChannel name used to relay OAuth completion from the popup
 *  back to the originating Settings tab. Same-origin only — eBay redirects
 *  back to our origin, so this works regardless of which window opened it. */
export const EBAY_OAUTH_CHANNEL = 'flipper-ebay-oauth';

/** localStorage key holding the in-flight OAuth context (state, redirectUri,
 *  marketplaceId, openedAt). Persists across the popup boundary unlike
 *  sessionStorage, which is per-window. */
export const EBAY_OAUTH_STORAGE_KEY = 'ebay_oauth_pending';

export function buildEbayAuthUrl(_redirectUri: string, state?: string): string {
  const clientId = import.meta.env.VITE_EBAY_CLIENT_ID as string | undefined;
  const ruName = import.meta.env.VITE_EBAY_RUNAME as string | undefined;
  if (!clientId)
    throw new Error(
      'VITE_EBAY_CLIENT_ID is not set. eBay OAuth will not work until this is configured.',
    );
  if (!ruName)
    throw new Error(
      'VITE_EBAY_RUNAME is not set. eBay OAuth will not work until this is configured.',
    );
  const host = isSandbox() ? 'auth.sandbox.ebay.com' : 'auth.ebay.com';
  const params = new URLSearchParams({
    client_id: clientId,
    redirect_uri: ruName,
    response_type: 'code',
    scope: EBAY_SCOPES,
  });
  // OAuth CSRF protection: bind the callback to the initiating tab
  if (state) params.set('state', state);
  return `https://${host}/oauth2/authorize?${params.toString()}`;
}

/** Lightweight authenticated round-trip to confirm the stored eBay tokens
 *  still work. Uses the `fetch-policies` proxy action which (a) requires a
 *  valid access token and (b) returns a stable per-policy-type count we can
 *  surface in the toast. Returns a shaped result rather than throwing so
 *  callers don't have to wrap in try/catch. */
export async function verifyEbayConnection(): Promise<{
  ok: boolean;
  error?: string;
  policies?: { fulfillment: number; payment: number; return: number };
}> {
  try {
    const result = await invokeProxy<{
      ok: true;
      policies: { fulfillment: string | null; payment: string | null; return: string | null };
      missing: string[];
    }>('fetch-policies');
    return {
      ok: true,
      policies: {
        fulfillment: result.policies.fulfillment ? 1 : 0,
        payment: result.policies.payment ? 1 : 0,
        return: result.policies.return ? 1 : 0,
      },
    };
  } catch (err) {
    return { ok: false, error: err instanceof Error ? err.message : 'Verification failed' };
  }
}

export async function getEbayConnectionStatus(): Promise<EbayConnectionStatus> {
  const { data } = await supabase
    .from('platform_connections')
    .select(
      'scope, created_at, fulfillment_policy_id, payment_policy_id, return_policy_id, policies_fetched_at, marketplace_id',
    )
    .eq('platform', 'ebay')
    .maybeSingle();
  if (!data) {
    return {
      connected: false,
      scope: null,
      connectedAt: null,
      marketplaceId: null,
      policiesReady: false,
      missingPolicies: [],
      policiesFetchedAt: null,
    };
  }
  // Cast via unknown because the generated Supabase types lag the
  // 20260510140000_platform_connections_policies +
  // 20260511120000_platform_connections_marketplace migrations.
  const row = data as unknown as {
    scope: string | null;
    created_at: string;
    fulfillment_policy_id: string | null;
    payment_policy_id: string | null;
    return_policy_id: string | null;
    policies_fetched_at: string | null;
    marketplace_id: string | null;
  };
  const missing: EbayConnectionStatus['missingPolicies'] = [];
  if (!row.fulfillment_policy_id) missing.push('fulfillment');
  if (!row.payment_policy_id) missing.push('payment');
  if (!row.return_policy_id) missing.push('return');
  return {
    connected: true,
    scope: row.scope,
    connectedAt: row.created_at,
    marketplaceId: row.marketplace_id,
    policiesReady: missing.length === 0,
    missingPolicies: missing,
    policiesFetchedAt: row.policies_fetched_at,
  };
}

export interface EbayOfferItem {
  offerId: string;
  sku: string;
  status: string;
  listingDescription?: string;
  buyerId?: string; // eBay buyer username; populated when a Best Offer is received
}

/**
 * An eBay return case as stored locally (after upsert by either the
 * list-returns proxy op or the ebay-webhook RETURN_* handler). Field
 * names mirror the `public.returns` columns rather than eBay's API
 * shape to keep the UI rendering loop straightforward.
 */
export interface EbayReturnCase {
  id: string;
  platform_return_id: string;
  sale_id: string | null;
  ebay_item_id: string | null;
  buyer_id: string;
  reason: string;
  reason_code: string | null;
  /** REQUESTED / OPEN / APPROVED / DENIED / ESCALATED / CLOSED / CANCELLED.
   *  Mixed across eBay's API surface; the UI maps to a small set of
   *  badges. */
  status: string;
  refund_amount: number | null;
  currency: string | null;
  requested_at: string;
  closed_at: string | null;
}

/**
 * A buyer Best Offer as returned by Trading API GetBestOffers.
 * Distinct from `EbayOfferItem` above (which is the seller-side
 * offer/listing configuration). Each `EbayBestOffer` is an actual
 * buyer offer awaiting seller response.
 */
export interface EbayBestOffer {
  /** BestOfferID — pass to acceptOffer/declineOffer/counterOffer. */
  offerId: string;
  /** eBay ItemID the offer is on. Also required by RespondToBestOffer. */
  itemId: string;
  /** Buyer's eBay username. */
  buyerId: string;
  /** Buyer's offered amount as a decimal string ('42.50'). */
  price: string;
  /** ISO 4217 currency the buyer offered in. */
  currency: string;
  /** Active / Pending / Expired / Accepted / Declined / etc. */
  status: string;
  /** ExpirationTime — when the offer auto-expires. */
  expiresAt: string | null;
}

export interface EbayOffersResult {
  offers?: EbayOfferItem[];
  total?: number;
}

/**
 * Typed error from ebay-proxy. Supabase's default `FunctionsHttpError`
 * stuffs the structured body into `.context` (a Response object) and
 * surfaces only "Edge Function returned a non-2xx status code" in
 * `.message`. We unwrap it here so every caller can branch on
 * `err.code` (e.g. `LISTING_NOT_FOUND`, `POLICIES_MISSING`).
 */
export class EbayProxyError extends Error {
  readonly code: string;
  readonly status: number;
  constructor(args: { code: string; message: string; status: number }) {
    super(args.message);
    this.code = args.code;
    this.status = args.status;
  }
}

async function invokeProxy<T>(action: string, payload?: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.functions.invoke('ebay-proxy', {
    body: { action, ...payload },
  });
  if (error) {
    // Supabase's FunctionsHttpError.context is the raw Response. Pull
    // out the JSON body's `error.{code,message}` if it exists; fall
    // back to the generic message for non-JSON responses.
    const ctx = (error as { context?: Response | undefined }).context;
    if (ctx && typeof ctx.json === 'function') {
      try {
        // Clone before reading — Supabase's error.context Response body
        // can only be consumed once, and callers may retry / re-throw.
        const parsed = (await ctx.clone().json()) as {
          error?: { code?: string; message?: string };
        };
        if (parsed?.error?.code) {
          throw new EbayProxyError({
            code: parsed.error.code,
            message: parsed.error.message ?? error.message,
            status: ctx.status,
          });
        }
      } catch (parseErr) {
        if (parseErr instanceof EbayProxyError) throw parseErr;
        // body wasn't JSON; fall through to generic throw
      }
    }
    throw error;
  }
  return data as T;
}

export const ebay = {
  oauthExchange(code: string, redirectUri: string, marketplaceId?: string) {
    return invokeProxy<{ ok: true }>('oauth-exchange', {
      code,
      redirect_uri: redirectUri,
      sandbox: isSandbox(),
      ...(marketplaceId ? { marketplace_id: marketplaceId } : {}),
    });
  },

  oauthRefresh() {
    return invokeProxy<{ ok: true }>('oauth-refresh');
  },

  disconnect() {
    return invokeProxy<{ ok: true }>('disconnect');
  },

  searchComps(query: string, limit = 40) {
    return invokeProxy<SearchCompsResult>('search-comps', { query, limit });
  },

  createListing(
    payload: EbayListingPayload,
    itemId?: string,
    listingData?: {
      title: string;
      description: string;
      features: string[];
      condition_statement: string;
    },
  ) {
    return invokeProxy<EbayListingResult>('create-listing', {
      // The proxy reads payload.item_id (for SKU, the listings-row upsert
      // guard, and the agent_actions audit), so item_id MUST be nested into
      // payload — a top-level item_id is never read server-side.
      payload: itemId
        ? { ...(payload as unknown as Record<string, unknown>), item_id: itemId }
        : (payload as unknown as Record<string, unknown>),
      ...(listingData ? { listing_data: listingData as unknown as Record<string, unknown> } : {}),
    });
  },

  /** Bulk update price and/or quantity across up to 25 SKUs in one
   *  call. Each item must include either sku or offerId, plus at
   *  least one of price or quantity. Returns per-item statusCode so
   *  the UI can highlight rows that failed. */
  /** eBay listing performance analytics: impressions, views,
   *  click-through, sell-through. Default window is last 30 days;
   *  pass startDate/endDate (YYYY-MM-DD) to override. Returns a
   *  per-listingId map of metric → value. */
  getListingAnalytics(args: { listingIds: string[]; startDate?: string; endDate?: string }) {
    return invokeProxy<{
      ok: true;
      range: { start: string; end: string };
      metrics: Record<string, Record<string, number | string | null>>;
      metricKeys: string[];
    }>('get-listing-analytics', {
      listing_ids: args.listingIds,
      ...(args.startDate ? { start_date: args.startDate } : {}),
      ...(args.endDate ? { end_date: args.endDate } : {}),
    });
  },

  bulkUpdatePriceQuantity(
    items: Array<{
      sku?: string;
      offerId?: string;
      price?: number;
      quantity?: number;
      /** ISO 4217 currency override for sellers with offers on more
       *  than one marketplace (Sprint 9). Falls back to the seller's
       *  bound marketplace currency when omitted. */
      currency?: string;
    }>,
  ) {
    return invokeProxy<{
      ok: boolean;
      total: number;
      succeeded: number;
      failed: number;
      failures: Array<{ sku?: string; statusCode: number; errors?: unknown[] }>;
    }>('bulk-update-price-quantity', { items });
  },

  /** End (withdraw) up to 50 active eBay listings in parallel. eBay
   *  has no bulk_withdraw endpoint so the proxy fans out with a
   *  5-at-a-time concurrency cap; total wall-clock for 50 offers is
   *  ~10s under normal latency. */
  /** List eBay merchant locations for the seller. Each location has
   *  a merchantLocationKey that's referenced from every offer; the
   *  defaultKey field marks which one new listings will use. */
  listLocations() {
    return invokeProxy<{
      ok: true;
      locations: Array<{
        merchantLocationKey: string;
        name?: string;
        location?: {
          address?: {
            addressLine1: string;
            addressLine2?: string;
            city: string;
            stateOrProvince: string;
            postalCode: string;
            country: string;
          };
        };
        merchantLocationStatus?: string;
        locationTypes?: string[];
        /** True when the location still carries the placeholder address the
         *  old OAuth bootstrap invented (fl-t1v). */
        isPlaceholder?: boolean;
      }>;
      total: number;
      defaultKey: string | null;
    }>('list-locations');
  },

  /** Create a location, or update an existing one with the same
   *  merchantLocationKey (address included). When set_as_default is true,
   *  the seller's platform_connections.merchant_location_key is updated. */
  upsertLocation(args: {
    locationKey: string;
    name: string;
    address: {
      addressLine1: string;
      addressLine2?: string;
      city: string;
      stateOrProvince: string;
      postalCode: string;
      country: string;
    };
    status?: 'ENABLED' | 'DISABLED';
    locationTypes?: Array<'WAREHOUSE' | 'STORE'>;
    locationInstructions?: string;
    setAsDefault?: boolean;
  }) {
    return invokeProxy<{ ok: true; locationKey: string; updated?: boolean; setAsDefault: boolean }>(
      'upsert-location',
      {
        location_key: args.locationKey,
        name: args.name,
        address: args.address,
        ...(args.status ? { status: args.status } : {}),
        ...(args.locationTypes ? { location_types: args.locationTypes } : {}),
        ...(args.locationInstructions ? { location_instructions: args.locationInstructions } : {}),
        ...(args.setAsDefault ? { set_as_default: true } : {}),
      },
    );
  },

  /** Set the default merchantLocationKey on the seller's
   *  platform_connections row. Used by new listings. */
  setDefaultLocation(locationKey: string) {
    return invokeProxy<{ ok: true; defaultKey: string }>('set-default-location', {
      location_key: locationKey,
    });
  },

  bulkEndListings(offerIds: string[]) {
    return invokeProxy<{
      ok: boolean;
      total: number;
      succeeded: number;
      failed: number;
      failures: Array<{ offerId: string; status: number; detail?: string }>;
    }>('bulk-end-listings', { offer_ids: offerIds });
  },

  /** Re-host a single image on eBay's Site-Hosted Pictures (EPS) via
   *  Trading API UploadSiteHostedPictures with ExternalPictureURL. eBay
   *  fetches the source URL once and returns a permanent
   *  https://i.ebayimg.com URL that stays alive independent of the
   *  source bucket. Use directly only for revise flows; for new
   *  listings pass `host_on_ebay: true` on the createListing payload
   *  to batch-upload at publish time. */
  uploadImageToEbay(args: { imageUrl: string; pictureName?: string }) {
    return invokeProxy<{ ok: true; fullUrl: string; baseUrl: string | null }>(
      'upload-image-to-ebay',
      {
        image_url: args.imageUrl,
        ...(args.pictureName ? { picture_name: args.pictureName } : {}),
      },
    );
  },

  /** Publish a multi-variant listing (Size/Color/etc. combos sharing
   *  one title). eBay requires every variation to declare the same
   *  set of aspect keys; the proxy enforces this up front so the
   *  cryptic 25001 doesn't surface mid-publish.
   *
   *  Returns the group SKU plus a list of `{sku, offerId, listingId}`
   *  for each child variation. Callers can use the listingIds to map
   *  back to local inventory_items rows. */
  createVariationListing(args: {
    groupSku: string;
    title: string;
    description: string;
    categoryId: string;
    imageUrls: string[];
    variations: Array<{
      sku: string;
      aspects: Record<string, string[]>;
      price: number;
      quantity: number;
      imageUrls?: string[];
    }>;
    /** Group-level aspects (Brand, Material — anything that applies
     *  to every variation). Per-variation aspects (Size, Color)
     *  belong on each `variations[].aspects`. */
    aspects?: Record<string, string[]>;
    condition?: string;
    merchantLocationKey?: string;
  }) {
    return invokeProxy<{
      ok: true;
      groupSku: string;
      variants: Array<{ sku: string; offerId: string; listingId: string }>;
    }>('create-variation-listing', {
      payload: {
        group_sku: args.groupSku,
        title: args.title,
        description: args.description,
        categoryId: args.categoryId,
        imageUrls: args.imageUrls,
        variations: args.variations,
        ...(args.aspects ? { aspects: args.aspects } : {}),
        ...(args.condition ? { condition: args.condition } : {}),
        ...(args.merchantLocationKey ? { merchantLocationKey: args.merchantLocationKey } : {}),
      },
    });
  },

  endListing(offerId: string, listingId: string) {
    return invokeProxy<{ ok: true }>('end-listing', { offer_id: offerId, listing_id: listingId });
  },

  listOffers(limit = 100) {
    return invokeProxy<EbayOffersResult>('list-offers', { limit });
  },

  /** Pull the seller's business policies from eBay and cache them on the
   *  user's platform_connections row. Called automatically post-OAuth, plus
   *  on demand from the Settings "Refresh policies" button. */
  fetchPolicies() {
    return invokeProxy<{
      ok: true;
      policies: { fulfillment: string | null; payment: string | null; return: string | null };
      missing: string[];
    }>('fetch-policies');
  },

  /** Hit eBay's taxonomy API for the top category suggestions for a query.
   *  Used by the listing review step so users pick a real categoryId. */
  suggestCategories(query: string) {
    return invokeProxy<{ ok: true; suggestions: EbayCategorySuggestion[] }>('suggest-categories', {
      query,
    });
  },

  /** Fetch the required item-specifics (aspects) for an eBay category.
   *  Without these, branded categories (e.g. Sneakers, Trading Cards)
   *  reject the offer publish. Used by EbayAspectsForm in the listing
   *  review step. */
  fetchRequiredAspects(categoryId: string) {
    return invokeProxy<{ ok: true; aspects: EbayRequiredAspect[] }>('fetch-required-aspects', {
      category_id: categoryId,
    });
  },

  /** List all pending buyer Best Offers across the seller's active
   *  listings. Calls Trading API GetBestOffers (no REST equivalent).
   *  Sprint 12 polls this every ~2 min; Sprint 20 webhook ingestion
   *  replaces polling with real-time push. */
  listBestOffers() {
    return invokeProxy<{ ok: true; offers: EbayBestOffer[]; total: number }>('list-best-offers');
  },

  /** Accept a buyer's Best Offer. Trading API RespondToBestOffer needs
   *  both the ItemID and BestOfferID — pass both. */
  acceptOffer(args: { offerId: string; itemId: string }) {
    return invokeProxy<{ ok: true; offerId: string; action: 'Accept' }>('accept-offer', {
      offer_id: args.offerId,
      item_id: args.itemId,
    });
  },

  /** Decline a buyer's Best Offer. */
  declineOffer(args: { offerId: string; itemId: string }) {
    return invokeProxy<{ ok: true; offerId: string; action: 'Decline' }>('decline-offer', {
      offer_id: args.offerId,
      item_id: args.itemId,
    });
  },

  /** Counter a buyer's Best Offer with a specific price. Currency
   *  defaults to USD; pass `currency` (ISO 4217) for non-US marketplaces. */
  counterOffer(args: { offerId: string; itemId: string; counterPrice: number; currency?: string }) {
    return invokeProxy<{ ok: true; offerId: string; action: 'Counter' }>('counter-offer', {
      offer_id: args.offerId,
      item_id: args.itemId,
      counter_price: args.counterPrice,
      ...(args.currency ? { currency: args.currency } : {}),
    });
  },

  /** Send an outbound Best Offer to the watchers of one of the seller's
   *  active listings (Trading API SendOfferToInterestedBuyers). eBay
   *  surfaces all current watchers who haven't bought; the offer is
   *  visible to them for 48h. Returns the persisted `outbound_offers`
   *  row including the platform_offer_id. Pro-tier gated in the UI. */
  sendOfferToWatchers(args: { itemId: string; offeredPrice: number; message?: string }) {
    return invokeProxy<{
      ok: true;
      offer: {
        id: string;
        user_id: string;
        listing_id: string | null;
        ebay_item_id: string;
        platform_offer_id: string | null;
        offered_price: number;
        original_price: number;
        discount_pct: number;
        expires_at: string | null;
        sent_at: string;
        response_status: string;
        response_at: string | null;
        accepted_buyer_id: string | null;
        created_at: string;
      };
    }>('send-offer-to-watchers', {
      ebay_item_id: args.itemId,
      offered_price: args.offeredPrice,
      ...(args.message ? { message: args.message } : {}),
    });
  },

  /** Revise a live eBay listing's price/quantity/title/description/aspects
   *  without ending + relisting. Pass only the fields that changed; the
   *  proxy GETs the current resource, merges, then PUTs.
   *
   *  Returns which side(s) were patched: `patched_item` for inventory_item
   *  (title/description/imageUrls/aspects/condition/quantity), `patched_offer`
   *  for offer-level changes (price). */
  reviseListing(args: {
    itemId: string;
    price?: number;
    quantity?: number;
    title?: string;
    description?: string;
    imageUrls?: string[];
    aspects?: Record<string, string[]>;
    condition?: string;
  }) {
    return invokeProxy<{
      ok: true;
      patched_item: boolean;
      patched_offer: boolean;
      ebay_listing_id: string | null;
    }>('revise-listing', {
      item_id: args.itemId,
      ...(args.price != null ? { price: args.price } : {}),
      ...(args.quantity != null ? { quantity: args.quantity } : {}),
      ...(args.title ? { title: args.title } : {}),
      ...(args.description ? { description: args.description } : {}),
      ...(args.imageUrls ? { imageUrls: args.imageUrls } : {}),
      ...(args.aspects ? { aspects: args.aspects } : {}),
      ...(args.condition ? { condition: args.condition } : {}),
    });
  },

  /** Sync inbound buyer messages from eBay into the local messages table.
   *  Idempotent: messages already stored (matched by eBay messageId) are
   *  updated in place, not duplicated. */
  listMessages(limit = 50) {
    return invokeProxy<{ ok: true; total: number; upserted: number; failed: number }>(
      'list-messages',
      { limit },
    );
  },

  /** Sync + list open eBay return cases. The proxy hits
   *  /post-order/v2/return/search, upserts every row into the local
   *  `returns` table, then reads the table back so the UI also sees
   *  rows produced by the webhook handler (which catch CLOSED states
   *  that fall outside an OPEN /search). Default scope is OPEN (action
   *  needed); pass `returnState: 'ALL'` for the full history. */
  listReturns(args: { returnState?: 'OPEN' | 'CLOSED' | 'ALL'; limit?: number } = {}) {
    return invokeProxy<{ ok: true; returns: EbayReturnCase[]; total: number; upserted: number }>(
      'list-returns',
      {
        ...(args.returnState ? { return_state: args.returnState } : {}),
        ...(args.limit ? { limit: args.limit } : {}),
      },
    );
  },

  /** Approve a buyer's return request — accepts the return and signals
   *  eBay to share the return shipping label flow with the buyer. */
  approveReturn(args: { returnId: string; comments?: string }) {
    return invokeProxy<{ ok: true; returnId: string; decision: 'APPROVE' }>('approve-return', {
      return_id: args.returnId,
      ...(args.comments ? { comments: args.comments } : {}),
    });
  },

  /** Deny a buyer's return request. eBay may auto-escalate the case
   *  into a managed-returns dispute if the buyer challenges; surface
   *  the comments in the response so we can audit the rationale. */
  denyReturn(args: { returnId: string; comments?: string }) {
    return invokeProxy<{ ok: true; returnId: string; decision: 'DECLINE' }>('deny-return', {
      return_id: args.returnId,
      ...(args.comments ? { comments: args.comments } : {}),
    });
  },

  /** Issue the refund on an approved return. Both refundAmount and
   *  currency must be passed together for a partial refund; omitting
   *  both refunds the full order total. */
  issueRefund(args: { returnId: string; refundAmount?: number; currency?: string }) {
    return invokeProxy<{
      ok: true;
      returnId: string;
      refundAmount: number | null;
      currency: string | null;
    }>('issue-refund', {
      return_id: args.returnId,
      ...(args.refundAmount != null ? { refund_amount: args.refundAmount } : {}),
      ...(args.currency ? { currency: args.currency } : {}),
    });
  },

  /** Reply to a buyer's eBay message. Trading API AddMemberMessageRTQ
   *  ("Respond To Question") needs the ItemID of the listing the
   *  question was about. Buyer routing is derived from ParentMessageID,
   *  so buyerId here is only used for the local outbound row display. */
  sendReply(args: {
    messageId: string;
    body: string;
    itemId: string;
    buyerId?: string;
    threadId?: string;
  }) {
    return invokeProxy<{ ok: true; warning?: string }>('send-reply', {
      message_id: args.messageId,
      body: args.body,
      item_id: args.itemId,
      ...(args.buyerId ? { buyer_id: args.buyerId } : {}),
      ...(args.threadId ? { thread_id: args.threadId } : {}),
    });
  },
};
